import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function CookiesPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">Cookiebeleid</h1>
          <p className="text-lg text-slate-600 mb-8">Laatst bijgewerkt: januari 2024</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">1. Wat zijn cookies?</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Cookies zijn kleine tekstbestanden die op uw computer of mobiele apparaat worden geplaatst 
                wanneer u onze website bezoekt. Cookies helpen ons om de website goed te laten functioneren 
                en uw gebruikservaring te verbeteren.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Er zijn verschillende soorten cookies: sommige zijn noodzakelijk voor het functioneren van 
                de website, andere helpen ons de website te verbeteren of u relevante informatie te tonen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">2. Welke cookies gebruiken wij?</h2>
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Functionele cookies (noodzakelijk)</h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Deze cookies zijn noodzakelijk voor het goed functioneren van onze website. Zonder deze 
                  cookies kan de website niet goed werken.
                </p>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 text-slate-900 font-semibold">Cookie</th>
                        <th className="text-left py-3 text-slate-900 font-semibold">Doel</th>
                        <th className="text-left py-3 text-slate-900 font-semibold">Bewaartijd</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 text-slate-700">session_id</td>
                        <td className="py-3 text-slate-700">Sessie beheer</td>
                        <td className="py-3 text-slate-700">Sessie</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-3 text-slate-700">cookie_consent</td>
                        <td className="py-3 text-slate-700">Opslaan cookie voorkeuren</td>
                        <td className="py-3 text-slate-700">1 jaar</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Analytische cookies (optioneel)</h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Deze cookies helpen ons te begrijpen hoe bezoekers onze website gebruiken. Deze informatie 
                  gebruiken wij om de website te verbeteren. Wij gebruiken deze cookies alleen met uw 
                  toestemming.
                </p>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700">
                    Op dit moment gebruiken wij geen analytische cookies. Mocht dit in de toekomst veranderen, 
                    dan zullen wij u hierover informeren en om uw toestemming vragen.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Marketing cookies (optioneel)</h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Marketing cookies worden gebruikt om bezoekers te volgen op verschillende websites. Het 
                  doel is om advertenties te tonen die relevant en interessant zijn voor de individuele 
                  gebruiker.
                </p>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700">
                    Wij gebruiken geen marketing cookies of tracking cookies van derden.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">3. Cookies van derden</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Onze website maakt gebruik van diensten van derden die cookies kunnen plaatsen:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>
                  <strong>Google Maps:</strong> Voor het tonen van kaarten en routebeschrijvingen. 
                  Google kan cookies plaatsen voor het functioneren van de kaart.
                </li>
              </ul>
              <p className="text-slate-700 leading-relaxed">
                Wij hebben geen controle over de cookies die door derden worden geplaatst. Raadpleeg de 
                privacyverklaringen van deze partijen voor meer informatie over hun cookies.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">4. Cookies beheren</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                U kunt zelf bepalen of u cookies wilt accepteren. De meeste browsers accepteren cookies 
                automatisch, maar u kunt uw browserinstellingen aanpassen om cookies te weigeren.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Let op: als u cookies uitschakelt, kan het zijn dat bepaalde delen van onze website niet 
                goed werken.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Cookies verwijderen of beheren per browser:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-wissen-websites-opgeslagen" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/nl-nl/microsoft-edge/cookies-verwijderen-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Microsoft Edge
                  </a>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">5. Wijzigingen in dit cookiebeleid</h2>
              <p className="text-slate-700 leading-relaxed">
                Wij kunnen dit cookiebeleid van tijd tot tijd aanpassen. De meest recente versie is altijd 
                te vinden op deze pagina. Wij raden u aan dit cookiebeleid regelmatig te raadplegen om op 
                de hoogte te blijven van eventuele wijzigingen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">6. Meer informatie</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Voor meer informatie over cookies en privacy kunt u de volgende websites bezoeken:
              </p>
              <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
                <li>
                  <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Autoriteit Persoonsgegevens
                  </a>
                </li>
                <li>
                  <a href="https://www.consumentenbond.nl/internet-privacy/cookies" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-600">
                    Consumentenbond - Cookies
                  </a>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">7. Contact</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Heeft u vragen over ons cookiebeleid? Neem dan contact met ons op:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-900 font-semibold mb-2">Utrecht Airport Taxi</p>
                <p className="text-slate-700">E-mail: info@utrechtairporttaxi.nl</p>
                <p className="text-slate-700">Telefoon: 06 1234 5678</p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
